import trim from 'lodash/trim';

import { ENTITY_ID_COLUMN_LABEL } from '@/modules/entity-management/constants/entityIdColumn.constant';
import {
    NormalizeExportCsvContext,
    resolveExportHeader,
} from '@/modules/entity-management/utils/normalize-export-csv/normalizeExportCsv';

const CONTACT_EMAIL_LABEL = 'Primary Contact Email';

export interface ExportLayoutSource {
    header: string[];
    rows: string[][];
}

/** Puts corrected rows back in the layout of the Download Data file they came from, so the corrected file
 * imports the same way.
 *
 * `worksheet` holds the rows under import labels, already in the Download Data encoding. Importable columns
 * take their values; read-only columns keep what was uploaded for the same entity, found by Entity ID or,
 * for a row without one, by contact email. */
export function buildExportLayoutRows(
    source: ExportLayoutSource,
    worksheet: string[][],
    ctx: Pick<NormalizeExportCsvContext, 'entityColumnsLabel' | 'keyToLabel'>,
): string[][] {
    const [ worksheetHeader = [], ...worksheetRows ] = worksheet;
    const sourceHeader = source.header.map(trim);
    const valueIndexes = source.header.map((label) => {
        const importLabel = resolveExportHeader(label, ctx);
        return importLabel === null ? -1 : worksheetHeader.indexOf(importLabel);
    });
    const indexBy = (label: string) => {
        const sourceIndex = sourceHeader.indexOf(label);
        const worksheetIndex = worksheetHeader.indexOf(label);
        const toKey = (value = '') => trim(value).toLowerCase();
        const rowsByKey = new Map(source.rows
            .filter((row) => sourceIndex >= 0 && toKey(row[sourceIndex]))
            .map((row) => [ toKey(row[sourceIndex]), row ]));
        return (row: string[]) => (worksheetIndex >= 0 && toKey(row[worksheetIndex])
            ? rowsByKey.get(toKey(row[worksheetIndex]))
            : undefined);
    };
    const findById = indexBy(ENTITY_ID_COLUMN_LABEL);
    const findByContactEmail = indexBy(CONTACT_EMAIL_LABEL);

    return [
        source.header,
        ...worksheetRows.map((row) => {
            const sourceRow = findById(row) || findByContactEmail(row);
            return source.header.map((_, index) => (valueIndexes[index] >= 0
                ? row[valueIndexes[index]] ?? ''
                : sourceRow?.[index] ?? ''));
        }),
    ];
}
